import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import CheckoutForm from './CheckoutForm'
import { LoaderContainer, PaymentInformationContainer } from './styles'
import PaymentOptions from './PaymentOptions'
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next'
import { Skeleton } from 'primereact/skeleton'

const CLIENT_SECRET = process.env.GATSBY_STRIPE_PUBLIC_KEY

const MIN_PAYMENT_AMOUNT = 5

const initialState = {
  hasSucceeded: false,
  error: '',
  isProcessing: false,
  isDisabled: true,
  clientSecret: '',
  selectedAmount: MIN_PAYMENT_AMOUNT
}

function reducer(state, action) {
  switch (action.type) {
    case 'enable':
      return {
        ...state,
        isDisabled: false,
      }
    case 'change':
      return {
        ...state,
        isDisabled: action.isDisabled,
        error: action.error,
        isProcessing: false
      }
    case 'gotClientSecret':
      return {
        ...state,
        clientSecret: action.clientSecret,
        isProcessing: false
      }

    case 'succeeded':
      return {
        ...state,
        hasSucceeded: true,
        error: '',
        isProcessing: false,
      }
    case 'processing':
      return {
        ...state,
        isProcessing: true,
      }
    case 'selectCurrency':
      return {
        ...state,
        selectedAmount: Math.max(action.amount, MIN_PAYMENT_AMOUNT)
      }
    default:
      return state
  }
}

const Payments = () => {
  const { language } = useI18next()
  const stripePromise = useMemo(() => loadStripe(CLIENT_SECRET, { locale: language }), [language])
  const [state, dispatch] = useReducer(reducer, initialState)
  const [remountCounter, setRemountCounter] = useState(0)
  const { t } = useTranslation()
  useEffect(() => {
    dispatch({ type: 'processing' })
    fetch(`${process.env.GATSBY_BACKEND_HOST || ''}/.netlify/functions/create-payment-intent`, {
      method: 'POST',
      body: JSON.stringify({
        amount: state.selectedAmount
      })
    })
      .then((res) => res.json())
      .then(((data) => dispatch({ type: 'gotClientSecret', clientSecret: data.clientSecret })))
  }, [state.selectedAmount])
  const options = useMemo(() =>
    state.clientSecret
      ? ({
        clientSecret: state.clientSecret
      })
      : undefined,
    [state.clientSecret])
  useEffect(() => {
    setRemountCounter((n) => n + 1)
  }, [options])
  const handleSelectCurrency = useCallback((amount) => {
    dispatch({ type: 'selectCurrency', amount })
  }, [])

  return (
    <div>
      <PaymentOptions selectedAmount={state.selectedAmount} onSelectCurrency={handleSelectCurrency} />
      <PaymentInformationContainer>
        <span>{t('aboutToDonate', { amount: state.selectedAmount, currency: t('currencyAbbreviated') })}</span>
      </PaymentInformationContainer>
      <div key={remountCounter}>
        {options
          ? (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm state={state} dispatch={dispatch} />
            </Elements>
          )
          : (
            <LoaderContainer>
              <Skeleton height='3rem' width='100%' />
              <Skeleton height='3rem' width='50%' />
              <Skeleton height='3rem' width='50%' />
              <Skeleton height='3rem' width='100%' />
            </LoaderContainer>
          )
        }
      </div>
    </div>
  )
}

export default Payments
